import React from 'react';

/**
 * PlotHoleCard
 * 
 * Renders a single collapsible plot hole card with severity indicator,
 * title, type badge, and expandable details (description, chapters,
 * related elements, suggested fix).
 * 
 * Props:
 * - hole: object - plot hole data { severity, title, type, description, affectedChapters, relatedElements, suggestedFix }
 * - index: number - card index
 * - expanded: boolean - whether details are visible
 * - onToggle: function(index) - callback to toggle expand/collapse
 * - actions: optional node rendered at the end of the expanded details
 * - levelLabel: optional label describing the hole level
 */
export default function PlotHoleCard({
  hole,
  index,
  expanded,
  onToggle,
  actions = null,
  levelLabel = 'Project-level',
}) {
  return (
    <div className={`plot-hole-card ${hole.severity}`}>
      <button
        type="button"
        className="plot-hole-header"
        onClick={() => onToggle(index)}
        aria-expanded={!!expanded}
      >
        <span className={`plot-hole-severity-dot ${hole.severity}`}>●</span>
        <span className="plot-hole-title">{hole.title}</span>
        <span className="plot-hole-level-badge">{levelLabel}</span>
        <span className="plot-hole-type-badge">{hole.type.replace(/_/g, ' ')}</span>
        <span className={`plot-hole-chevron ${expanded ? 'expanded' : ''}`}>›</span>
      </button>
      {expanded && (
        <div className="plot-hole-details">
          <p className="plot-hole-description">{hole.description}</p>
          <div className="plot-hole-meta">
            {hole.affectedChapters && hole.affectedChapters.length > 0 && (
              <div className="plot-hole-chapters">
                <span className="plot-hole-meta-label">Chapters:</span>
                {hole.affectedChapters.map((ch, i) => (
                  <span key={i} className="plot-hole-chapter-num">Ch {ch}</span>
                ))}
              </div>
            )}
            {hole.relatedElements && hole.relatedElements.length > 0 && (
              <div className="plot-hole-elements">
                <span className="plot-hole-meta-label">Related:</span>
                {hole.relatedElements.map((el, i) => (
                  <span key={i} className="plot-hole-element-tag">{el}</span>
                ))}
              </div>
            )}
          </div>
          {hole.suggestedFix && (
            <div className="plot-hole-fix">
              <span className="plot-hole-fix-label">Suggested fix:</span>
              <p className="plot-hole-fix-text">{hole.suggestedFix}</p>
            </div>
          )}
          {actions}
        </div>
      )}
    </div>
  );
}
